import request from "@/lib/api/handler";

import { useAuth0 } from "@auth0/auth0-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { QueryKeys } from "@/queries/utils";
import {
  AdminJobCandidateDetails,
  AdminJobDetails,
} from "@/queries/admin/job-details";

export const useAccelerateOptInReminderEmail = ({
  openingId,
  candidateStatusId,
  optInReminderId,
  onSuccessCb,
}: {
  openingId: string;
  candidateStatusId: string;
  optInReminderId: string;
  onSuccessCb?: (data: AdminJobCandidateDetails) => void;
}) => {
  const qc = useQueryClient();
  const { getAccessTokenSilently } = useAuth0();

  const { mutate, isPending, error } = useMutation({
    mutationFn: async () => {
      const token = await getAccessTokenSilently();

      return request<AdminJobCandidateDetails>({
        method: "POST",
        path: `/api/admin/candidate_status/${candidateStatusId}/reminders/${optInReminderId}/accelerate`,
        token,
      });
    },
    onSuccess: (data) => {
      qc.setQueryData(
        QueryKeys.adminJobDetails(openingId),
        (oldData?: AdminJobDetails) => {
          // If there is existing data, update it
          if (oldData) {
            // Make a copy and check find the updated element
            const candidates = [...oldData.candidates];
            const candidateIndex = candidates.findIndex(
              (candidate) => candidate.candidateId === data.candidateId,
            );

            // If the element is found
            if (candidateIndex !== -1 && candidates[candidateIndex]) {
              // Update it
              candidates[candidateIndex] = data;

              return {
                ...oldData,
                candidates,
              };
            }
          }

          // If the entry couldn't be found, invalidate and fetch new data
          void qc.invalidateQueries({
            queryKey: QueryKeys.adminJobDetails(openingId),
          });
        },
      );

      onSuccessCb?.(data);
    },
  });

  return {
    accelerateOptInReminderEmail: mutate,
    isAccelerateOptInReminderEmail: isPending,
    accelerateOptInReminderEmailError: error,
  };
};
